import axios from "axios";
import { BASE_API_URL } from "../consts";
import type { ApartmentMealSetting, MealSelection } from "./mealsAPI";

// ─── Types ─────────────────────────────────────────────────────────────────────

export type ApartmentType = "studio" | "apartment" | "loft" | "villa" | "house";
export type ApartmentCurrency = "RSD" | "EUR" | "BAM";
export type PricingRuleType = "discount" | "custom";
export type BookingWindowMode = "unlimited" | "fixed_date" | "rolling";

export interface BookingWindow {
  mode: BookingWindowMode;
  fixedDate?: string | null;  // YYYY-MM-DD
  rollingDays?: number | null;
}

export interface PricingRule {
  _id?: string;
  type: PricingRuleType;
  label?: string;
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
  value: number; // % for discount, price/night for custom
}

export interface LastMinuteRule {
  enabled: boolean;
  daysThreshold: number; // rezerviši unutar X dana do check-ina
  discount: number;      // % popust
  label?: string;
}

export interface EarlyBookingRule {
  enabled: boolean;
  daysThreshold: number; // rezerviši X+ dana unaprijed
  discount: number;      // % popust
  label?: string;
}

export interface WeekdayRule {
  _id?: string;
  days: number[]; // 0=Ned, 1=Pon, 2=Uto, 3=Sri, 4=Čet, 5=Pet, 6=Sub
  type: PricingRuleType;
  value: number;
  label?: string;
}

export interface NightCountRule {
  _id?: string;
  nights: number;        // tačan broj noći na koje se primjenjuje pravilo
  pricePerNight: number; // cijena po noći za ovaj broj noći
  label?: string;
}

export interface ApartmentExtra {
  _id?: string;
  name: string;
  regularPrice: number;
  discountedPrice?: number | null;
  unit?: string; // "po osobi", "po stavci", itd.
}

export interface ReservationExtra {
  extraId?: string;
  name: string;
  quantity: number;
  pricePerUnit: number;
  total: number;
}

export type AptReservationStatus =
  | "confirmed"
  | "pending"
  | "staying"
  | "checkout"
  | "canceled"
  | "noshow";

export type AptReservationChannel = "direct" | "booking" | "airbnb" | "phone" | "other";
export type AptReservationType = "standard" | "blogger" | "discount" | "private";
export type AptPaymentStatus = "unpaid" | "partial" | "paid";
export type AptEventType = "blogger" | "discount";

export interface BlockedPeriod {
  _id?: string;
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD inclusive
  reason?: string;
}

export interface ClientApartment {
  _id: string;
  clientId: string;
  name: string;
  location: string;
  description?: string;
  pricePerNight: number;
  minNights: number;
  bookingWindow?: BookingWindow;
  capacity: number;
  tags: string[];
  amenities: string[];
  images: string[];
  coverGradient?: string;
  type: ApartmentType;
  isActive: boolean;
  currency: ApartmentCurrency;
  pricingRules: PricingRule[];
  weekdayRules: WeekdayRule[];
  nightCountRules?: NightCountRule[];
  lastMinute?: LastMinuteRule;
  earlyBooking?: EarlyBookingRule;
  extras: ApartmentExtra[];
  meals: ApartmentMealSetting[];
  menuCategories?: { categoryId: string; isActive: boolean }[];
  blockedPeriods?: BlockedPeriod[];
  createdAt: string;
  updatedAt: string;
}

export interface AptReservation {
  _id: string;
  clientId: string;
  apartmentId: string | { _id: string; name: string; location: string; coverGradient?: string };
  guestName: string;
  guestEmail?: string;
  guestPhone?: string;
  instagramName?: string | null;
  checkIn: string;
  checkOut: string;
  adults: number;
  children: number;
  channel: AptReservationChannel;
  status: AptReservationStatus;
  amount: number;
  isPaid: boolean;
  paymentStatus: AptPaymentStatus;
  paidAmount: number;
  type: AptReservationType;
  note?: string;
  extras: ReservationExtra[];
  referralCode?: string | null;
  promoCode?: string | null;
  promoDiscount?: number;
  baseAmount?: number | null;
  packageId?: string | null;
  packageName?: string | null;
  mealSelections?: MealSelection[];
  mealsTotal?: number;
  // Optional override: QR-menu categories these guests see during their stay. Empty/unset = apartment default.
  menuCategoryIds?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AptEventExtra {
  label: string;
  cls: "free" | "paid" | "";
}

export interface AptEvent {
  _id: string;
  clientId: string;
  type: AptEventType;
  title: string;
  sub?: string;
  apartmentName?: string;
  apartmentId?: string | { _id: string; name: string };
  startDate?: string;
  endDate?: string;
  nights: number;
  cost: number;
  extraCost: number;
  guestPayment?: number; // fee we pay to the person — not counted in revenue stats
  currency?: ApartmentCurrency;
  archived?: boolean;
  extras: AptEventExtra[];
  deliverables?: string;
  contact?: string;
  createdAt: string;
}

export interface AptApartmentStat {
  id: string;
  name: string;
  currency: ApartmentCurrency;
  reservations: number;
  occupiedNights: number;
  occupancyRate: number;
  revenue: number;
  adr: number;
}

export interface AptChannelStat {
  channel: AptReservationChannel;
  count: number;
  revenue: number;
}

export interface AptStats {
  occupancyRate: number;
  totalReservations: number;
  revenue: number;
  paidRevenue: number;
  pendingRevenue: number;
  freeNights: number;
  totalNights: number;
  occupiedNights: number;
  apartments: number;
  apartmentStats?: AptApartmentStat[];
  channels?: AptChannelStat[];
}

// ─── Apartments ────────────────────────────────────────────────────────────────

export const getClientApartments = (clientId: string) =>
  axios.get<ClientApartment[]>(`${BASE_API_URL}/${clientId}/client-apartments`);

export const getClientApartmentById = (id: string) =>
  axios.get<ClientApartment>(`${BASE_API_URL}/client-apartments/${id}`);

export const createClientApartment = (
  clientId: string,
  data: Partial<ClientApartment>
) => axios.post<ClientApartment>(`${BASE_API_URL}/${clientId}/client-apartments`, data);

export const updateClientApartment = (id: string, data: Partial<ClientApartment>) =>
  axios.put<ClientApartment>(`${BASE_API_URL}/client-apartments/${id}`, data);

export const deleteClientApartment = (id: string) =>
  axios.delete(`${BASE_API_URL}/client-apartments/${id}`);

// ─── Reservations ──────────────────────────────────────────────────────────────

export interface GetAptReservationsParams {
  from?: string;
  to?: string;
  status?: AptReservationStatus | "all";
  apartmentId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const getAptReservations = (
  clientId: string,
  params: GetAptReservationsParams = {}
) =>
  axios.get<{ reservations: AptReservation[]; total: number }>(
    `${BASE_API_URL}/${clientId}/apt-reservations`,
    { params }
  );

export const getAptReservationById = (id: string) =>
  axios.get<AptReservation>(`${BASE_API_URL}/apt-reservations/${id}`);

export const createAptReservation = (
  clientId: string,
  data: Partial<AptReservation>
) => axios.post<AptReservation>(`${BASE_API_URL}/${clientId}/apt-reservations`, data);

export const updateAptReservation = (id: string, data: Partial<AptReservation>) =>
  axios.put<AptReservation>(`${BASE_API_URL}/apt-reservations/${id}`, data);

export const updateAptReservationStatus = (
  id: string,
  status: AptReservationStatus
) =>
  axios.patch<AptReservation>(`${BASE_API_URL}/apt-reservations/${id}/status`, {
    status,
  });

export const deleteAptReservation = (id: string) =>
  axios.delete(`${BASE_API_URL}/apt-reservations/${id}`);

export const getAptStats = (clientId: string, year?: number, month?: number) =>
  axios.get<AptStats>(`${BASE_API_URL}/${clientId}/apt-reservations/stats`, {
    params: { year, month },
  });

export interface MonthStats {
  month: number;
  occupancyRate: number;
  totalReservations: number;
  revenue: number;
  paidRevenue: number;
  pendingRevenue: number;
  occupiedNights: number;
  totalNights: number;
  freeNights: number;
}

export interface YearlyStats {
  year: number;
  apartments: number;
  months: MonthStats[];
}

export const getAptYearlyStats = (clientId: string, year: number) =>
  axios.get<YearlyStats>(`${BASE_API_URL}/${clientId}/apt-reservations/yearly-stats`, {
    params: { year },
  });

// ─── Events ────────────────────────────────────────────────────────────────────

export const getAptEvents = (clientId: string, type?: AptEventType) =>
  axios.get<AptEvent[]>(`${BASE_API_URL}/${clientId}/apt-events`, {
    params: type ? { type } : {},
  });

export const createAptEvent = (clientId: string, data: Partial<AptEvent>) =>
  axios.post<AptEvent>(`${BASE_API_URL}/${clientId}/apt-events`, data);

export const updateAptEvent = (id: string, data: Partial<AptEvent>) =>
  axios.put<AptEvent>(`${BASE_API_URL}/apt-events/${id}`, data);

export const archiveAptEvent = (id: string, archived: boolean) =>
  axios.patch<AptEvent>(`${BASE_API_URL}/apt-events/${id}/archive`, { archived });

export const deleteAptEvent = (id: string) =>
  axios.delete(`${BASE_API_URL}/apt-events/${id}`);

// ─── Referrals ──────────────────────────────────────────────────────────────

export type ReferralRewardType = "discount" | "free_nights";
export type ReferralThresholdType = "bookings" | "nights";

export interface ReferralReward {
  _id?: string;
  type: ReferralRewardType;
  thresholdType: ReferralThresholdType;
  threshold: number;  // min bookings or nights to unlock
  value: number;      // % for discount, count for free_nights
  label?: string;
}

export interface AptReferral {
  _id: string;
  clientId: string;
  name: string;
  code: string;
  description?: string;
  isActive: boolean;
  rewards: ReferralReward[];
  totalBookings: number;
  totalRevenue: number;
  totalNights: number;
  createdAt: string;
  updatedAt: string;
}

export const getAptReferrals = (clientId: string) =>
  axios.get<AptReferral[]>(`${BASE_API_URL}/${clientId}/apt-referrals`);

export const createAptReferral = (
  clientId: string,
  data: { name: string; code: string; description?: string; rewards?: ReferralReward[] }
) => axios.post<AptReferral>(`${BASE_API_URL}/${clientId}/apt-referrals`, data);

export const updateAptReferral = (id: string, data: Partial<AptReferral>) =>
  axios.put<AptReferral>(`${BASE_API_URL}/apt-referrals/${id}`, data);

export const deleteAptReferral = (id: string) =>
  axios.delete(`${BASE_API_URL}/apt-referrals/${id}`);

// ─── Guests ────────────────────────────────────────────────────────────────────

export interface AptGuestReservation {
  _id: string;
  apartmentName?: string;
  currency: ApartmentCurrency;
  checkIn: string;
  checkOut: string;
  amount: number;
  status: AptReservationStatus;
  channel: AptReservationChannel;
  adults: number;
  children: number;
  note?: string;
  isPaid: boolean;
  paymentStatus: AptPaymentStatus;
  paidAmount: number;
}

export interface AptGuest {
  key: string;
  guestName: string;
  guestEmail?: string | null;
  guestPhone?: string | null;
  totalStays: number;
  totalNights: number;
  totalSpent: number;
  totalPaid: number;
  spentByCurrency: Record<string, number>;
  paidByCurrency: Record<string, number>;
  lastStay: string | null;
  firstStay: string | null;
  favoriteApartment: { id: string; name: string } | null;
  channels: AptReservationChannel[];
  recentReservations: AptGuestReservation[];
}

export interface GetAptGuestsParams {
  search?: string;
  sortBy?: "lastStay" | "totalSpent" | "totalStays" | "totalNights" | "guestName";
  order?: "asc" | "desc";
  page?: number;
  limit?: number;
}

export const getAptGuests = (clientId: string, params: GetAptGuestsParams = {}) =>
  axios.get<{ guests: AptGuest[]; total: number }>(
    `${BASE_API_URL}/${clientId}/apt-guests`,
    { params }
  );

// ─── Expenses ──────────────────────────────────────────────────────────────────

export type AptExpenseCategory =
  | "maintenance"
  | "cleaning"
  | "utilities"
  | "supplies"
  | "commission"
  | "tax"
  | "marketing"
  | "insurance"
  | "other";

export interface AptExpense {
  _id: string;
  clientId: string;
  apartmentId?: string | { _id: string; name: string } | null;
  date: string;
  category: AptExpenseCategory;
  description: string;
  amount: number;
  currency: ApartmentCurrency;
  note?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GetAptExpensesParams {
  from?: string;
  to?: string;
  apartmentId?: string;
  category?: AptExpenseCategory;
}

export const getAptExpenses = (clientId: string, params: GetAptExpensesParams = {}) =>
  axios.get<AptExpense[]>(`${BASE_API_URL}/${clientId}/apt-expenses`, { params });

export const createAptExpense = (clientId: string, data: Partial<AptExpense>) =>
  axios.post<AptExpense>(`${BASE_API_URL}/${clientId}/apt-expenses`, data);

export const updateAptExpense = (id: string, data: Partial<AptExpense>) =>
  axios.put<AptExpense>(`${BASE_API_URL}/apt-expenses/${id}`, data);

export const deleteAptExpense = (id: string) =>
  axios.delete(`${BASE_API_URL}/apt-expenses/${id}`);

// ─── Additional Income ─────────────────────────────────────────────────────────

export type AptIncomeCategory =
  | "quad"
  | "food"
  | "drinks"
  | "transfer"
  | "tour"
  | "equipment"
  | "sport"
  | "other";

export interface AptIncome {
  _id: string;
  clientId: string;
  apartmentId?: string | { _id: string; name: string } | null;
  date: string;
  category: AptIncomeCategory;
  description: string;
  amount: number;
  currency: ApartmentCurrency;
  isPaid: boolean;
  note?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GetAptIncomesParams {
  from?: string;
  to?: string;
  apartmentId?: string;
  category?: AptIncomeCategory;
}

export const getAptIncomes = (clientId: string, params: GetAptIncomesParams = {}) =>
  axios.get<AptIncome[]>(`${BASE_API_URL}/${clientId}/apt-incomes`, { params });

export const createAptIncome = (clientId: string, data: Partial<AptIncome>) =>
  axios.post<AptIncome>(`${BASE_API_URL}/${clientId}/apt-incomes`, data);

export const updateAptIncome = (id: string, data: Partial<AptIncome>) =>
  axios.put<AptIncome>(`${BASE_API_URL}/apt-incomes/${id}`, data);

export const deleteAptIncome = (id: string) =>
  axios.delete(`${BASE_API_URL}/apt-incomes/${id}`);

// ─── Promo Codes ───────────────────────────────────────────────────────────────

export type PromoCodeType = "percent" | "fixed";

export interface AptPromoCode {
  _id: string;
  clientId: string;
  code: string;
  type: PromoCodeType;
  value: number;
  description?: string;
  isActive: boolean;
  validFrom?: string | null;
  validTo?: string | null;
  maxUses?: number | null;
  usedCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface PromoValidateResult {
  valid: boolean;
  promoId?: string;
  type?: PromoCodeType;
  value?: number;
  description?: string;
  discount?: number;
  finalAmount?: number;
  message?: string;
}

export const getAptPromoCodes = (clientId: string) =>
  axios.get<AptPromoCode[]>(`${BASE_API_URL}/${clientId}/apt-promo-codes`);

export const createAptPromoCode = (clientId: string, data: Partial<AptPromoCode>) =>
  axios.post<AptPromoCode>(`${BASE_API_URL}/${clientId}/apt-promo-codes`, data);

export const updateAptPromoCode = (id: string, data: Partial<AptPromoCode>) =>
  axios.put<AptPromoCode>(`${BASE_API_URL}/apt-promo-codes/${id}`, data);

export const deleteAptPromoCode = (id: string) =>
  axios.delete(`${BASE_API_URL}/apt-promo-codes/${id}`);

export const validatePromoCode = (code: string, clientId: string, amount: number) =>
  axios.post<PromoValidateResult>(`${BASE_API_URL}/public/promo/validate`, { code, clientId, amount });

// ─── Packages / Akcije ─────────────────────────────────────────────────────────

export interface AptPackageInclude {
  label: string;
  icon?: string;
}

export interface AptPackagePricingTier {
  persons: number;
  pricePerNight: number;
}

export interface AptPackageChildRule {
  ageFrom: number;
  ageTo: number | null;  // null = no upper bound
  priceType: "free" | "fixed" | "adult";
  pricePerNight?: number | null;  // only for "fixed"
}

export interface AptPackage {
  _id: string;
  clientId: string;
  apartmentId?: string | null;
  apartmentName?: string;
  name: string;
  description?: string;
  validFrom: string;   // YYYY-MM-DD
  validTo: string;     // YYYY-MM-DD
  minNights: number;
  includes: AptPackageInclude[];
  pricePerNight?: number | null;
  pricingByPersons?: AptPackagePricingTier[];
  childrenPricing?: AptPackageChildRule[];
  discountPercent?: number | null;
  currency?: ApartmentCurrency;
  isActive: boolean;
  archived?: boolean;
  createdAt: string;
  updatedAt: string;
}

export const getAptPackages = (clientId: string) =>
  axios.get<AptPackage[]>(`${BASE_API_URL}/${clientId}/apt-packages`);

export const createAptPackage = (clientId: string, data: Partial<AptPackage>) =>
  axios.post<AptPackage>(`${BASE_API_URL}/${clientId}/apt-packages`, data);

export const updateAptPackage = (id: string, data: Partial<AptPackage>) =>
  axios.put<AptPackage>(`${BASE_API_URL}/apt-packages/${id}`, data);

export const deleteAptPackage = (id: string) =>
  axios.delete(`${BASE_API_URL}/apt-packages/${id}`);

export const getPublicAptPackages = (clientId: string, apartmentId?: string) =>
  axios.get<AptPackage[]>(`${BASE_API_URL}/public/apt-packages`, {
    params: { clientId, ...(apartmentId ? { apartmentId } : {}) },
  });
